/**
 * Comprehensive Performance Testing Utility
 * Measures page loads, API calls, renders, and Web Vitals
 */

interface PerformanceMetrics {
  pageName: string;
  timestamp: number;
  metrics: {
    // Navigation timing
    navigationStart: number;
    domContentLoaded: number;
    loadComplete: number;
    
    // Resource timing
    apiCalls: {
      url: string;
      duration: number;
      size: number;
      status: number;
    }[];
    totalApiCalls: number;
    totalApiTime: number;
    
    // Memory (Chrome only)
    memoryUsage?: {
      usedJSHeapSize: string;
      totalJSHeapSize: string;
    };
    
    // Web Vitals
    lcp?: number;
    fid?: number;
    cls?: number;
  };
}

class PerformanceTest {
  private metrics: PerformanceMetrics[] = [];
  private currentPageMetrics: Partial<PerformanceMetrics> | null = null;
  private startTime: number = 0;

  /**
   * Start monitoring a page
   */
  startPageTest(pageName: string) {
    this.startTime = performance.now();
    this.currentPageMetrics = {
      pageName,
      timestamp: Date.now(),
      metrics: {
        navigationStart: 0,
        domContentLoaded: 0,
        loadComplete: 0,
        apiCalls: [],
        totalApiCalls: 0,
        totalApiTime: 0,
      },
    };

    // Performance test started
  }

  /**
   * Capture all network requests
   */
  captureNetworkRequests() {
    if (!this.currentPageMetrics) return;

    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const apiCalls = resources
      .filter(r => r.initiatorType === 'fetch' || r.initiatorType === 'xmlhttprequest')
      .map(r => ({
        url: r.name,
        duration: r.duration,
        size: r.transferSize || 0,
        status: 200, // Can't get status from PerformanceResourceTiming
      }));

    if (this.currentPageMetrics.metrics) {
      this.currentPageMetrics.metrics.apiCalls = apiCalls;
      this.currentPageMetrics.metrics.totalApiCalls = apiCalls.length;
      this.currentPageMetrics.metrics.totalApiTime = apiCalls.reduce(
        (sum, call) => sum + call.duration,
        0
      );
    }
  }

  /**
   * Capture navigation timing
   */
  captureNavigationTiming() {
    if (!this.currentPageMetrics?.metrics) return;

    const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navTiming) {
      this.currentPageMetrics.metrics.navigationStart = navTiming.fetchStart;
      this.currentPageMetrics.metrics.domContentLoaded = navTiming.domContentLoadedEventEnd - navTiming.fetchStart;
      this.currentPageMetrics.metrics.loadComplete = navTiming.loadEventEnd - navTiming.fetchStart;
    }
  }

  /**
   * Capture memory usage
   */
  captureMemory() {
    if (!this.currentPageMetrics?.metrics) return;

    if ('memory' in performance) {
      const memory = (performance as any).memory;
      this.currentPageMetrics.metrics.memoryUsage = {
        usedJSHeapSize: (memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB',
        totalJSHeapSize: (memory.totalJSHeapSize / 1048576).toFixed(2) + ' MB',
      };
    }
  }

  /**
   * Capture Web Vitals
   */
  captureWebVitals() {
    if (!this.currentPageMetrics?.metrics) return;

    // LCP
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (this.currentPageMetrics?.metrics) {
        this.currentPageMetrics.metrics.lcp = lastEntry.startTime;
      }
    });

    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // LCP not supported
    }

    // CLS
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
      if (this.currentPageMetrics?.metrics) {
        this.currentPageMetrics.metrics.cls = clsValue;
      }
    });

    try {
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      // CLS not supported
    }

    // FID
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        const fid = entry.processingStart - entry.startTime;
        if (this.currentPageMetrics?.metrics) {
          this.currentPageMetrics.metrics.fid = fid;
        }
      });
    });

    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      // FID not supported
    }
  }

  /**
   * End page test and save metrics
   */
  endPageTest() {
    if (!this.currentPageMetrics) return;

    const endTime = performance.now();
    const totalTime = endTime - this.startTime;

    // Capture all metrics
    this.captureNavigationTiming();
    this.captureNetworkRequests();
    this.captureMemory();
    this.captureWebVitals();

    // Save metrics
    this.metrics.push(this.currentPageMetrics as PerformanceMetrics);

    // Log results - performance test complete
    const metrics = this.currentPageMetrics.metrics;
    
    // Performance tracking available

    this.currentPageMetrics = null;
  }

  /**
   * Get all recorded metrics
   */
  getAllMetrics() {
    return this.metrics;
  }

  /**
   * Generate performance report
   */
  generateReport() {
    // Performance report available
    
    if (this.metrics.length === 0) {
      return;
    }

    // Calculate averages
    const avgApiCalls = this.metrics.reduce((sum, m) => sum + m.metrics.totalApiCalls, 0) / this.metrics.length;
    const avgApiTime = this.metrics.reduce((sum, m) => sum + m.metrics.totalApiTime, 0) / this.metrics.length;
    const avgDomLoad = this.metrics.reduce((sum, m) => sum + m.metrics.domContentLoaded, 0) / this.metrics.length;

    // Metrics tracked internally
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = [];
    // Performance metrics cleared
  }
}

// Export singleton instance
export const performanceTest = new PerformanceTest();

// Make available globally for console access
if (typeof window !== 'undefined') {
  (window as any).performanceTest = performanceTest;
  // Performance test available globally
}
